import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Chip,
  IconButton,
  Tooltip,
  TablePagination,
  TextField,
  MenuItem,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Container,
  Avatar,
  Stack,
  Fade,
  Zoom,
  // LinearProgress,
  Card,
  CardContent,
  alpha,
  useTheme
  // useMediaQuery,
} from '@mui/material';
import {
  Visibility,
  Security,
  Block,
  Warning,
  CheckCircle,
  Close,
  AccessTime,
  Memory,
  Gavel,
  Shield,
  History as HistoryIcon,
  TrendingUp,
  Speed,
  Refresh,
  FilterList,
  Assessment,
  VerifiedUser,
  Info
} from '@mui/icons-material';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

interface PromptRecord {
  id: number;
  prompt_preview: string;
  response_preview?: string;
  status: 'allowed' | 'blocked' | 'flagged';
  threat_score: number;
  threats_count: number;
  tokens_used?: number;
  model?: string;
  processing_time_ms?: number;
  created_at: string;
  detected_threats?: Array<{
    type: string;
    confidence: number;
    category?: string;
    framework?: string;
  }>;
}

interface PromptDetail {
  id: number;
  prompt_text: string;
  filtered_prompt?: string;
  response_text?: string;
  filtered_response?: string;
  status: string;
  threat_score: number;
  detected_threats: Array<{
    type: string;
    confidence: number;
    category?: string;
  }>;
  tokens_used: number;
  model: string;
  processing_time_ms: number;
  metadata?: any;
  created_at: string;
}

interface HistoryResponse {
  prompts: PromptRecord[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

const PromptHistory: React.FC = () => {
  const theme = useTheme();
  // const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [prompts, setPrompts] = useState<PromptRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [frameworkFilter, setFrameworkFilter] = useState<string>('all');
  const [selectedPrompt, setSelectedPrompt] = useState<PromptDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);
  const { isAuthenticated } = useAuth();

  useEffect(() => {
    if (isAuthenticated) {
      fetchPromptHistory();
    }
  }, [isAuthenticated, page, rowsPerPage, statusFilter]);

  const fetchPromptHistory = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: (page + 1).toString(),
        limit: rowsPerPage.toString()
      });

      if (statusFilter !== 'all') {
        params.append('status', statusFilter);
      }

      const response = await axios.get(`/api/prompt/history?${params}`);
      const data: HistoryResponse = response.data;

      setPrompts(data.prompts || []);
      setTotalCount(data.pagination?.total || 0);
      setError(null);
    } catch (err: any) {
      console.error('Error fetching prompt history:', err);
      setError('Failed to load prompt history');
      setPrompts([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  const fetchPromptDetail = async (promptId: number) => {
    try {
      setDetailLoading(true);
      setDetailError(null);
      const response = await axios.get(`/api/prompt/${promptId}`);
      setSelectedPrompt(response.data);
    } catch (err: any) {
      console.error('Error fetching prompt detail:', err);
      setDetailError('Failed to load prompt details');
    } finally {
      setDetailLoading(false);
    }
  };

  const handleViewDetails = (promptId: number) => {
    fetchPromptDetail(promptId);
  };

  const handleCloseDetail = () => {
    setSelectedPrompt(null);
    setDetailError(null);
  };

  const getStatusColor = (status: string): 'error' | 'warning' | 'success' | 'info' => {
    switch (status) {
      case 'blocked':
        return 'error';
      case 'flagged':
        return 'warning';
      case 'allowed':
        return 'success';
      default:
        return 'info';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'blocked':
        return <Block fontSize="small" />;
      case 'flagged':
        return <Warning fontSize="small" />;
      case 'allowed':
        return <CheckCircle fontSize="small" />;
      default:
        return <Security fontSize="small" />;
    }
  };

  const getThreatColor = (score: number) => {
    if (score >= 0.8) return 'error';
    if (score >= 0.5) return 'warning';
    return 'success';
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  // const truncateText = (text: string, maxLength: number = 100) => {
  //   if (text.length <= maxLength) return text;
  //   return text.substring(0, maxLength) + '...';
  // };

  const getComplianceFrameworks = (
    threats: Array<{ type: string; category?: string; framework?: string }> = []
  ) => {
    const frameworks = new Set<string>();

    threats.forEach((threat) => {
      // Check if threat has explicit framework
      if (threat.framework) {
        frameworks.add(threat.framework);
      }
      // Infer framework from threat type
      else if (threat.type) {
        const type = threat.type.toLowerCase();
        if (
          type.includes('phi') ||
          type.includes('hipaa') ||
          type.includes('patient') ||
          type.includes('medical')
        ) {
          frameworks.add('HIPAA');
        }
        if (
          type.includes('gdpr') ||
          type.includes('pii') ||
          type.includes('personal') ||
          type.includes('consent')
        ) {
          frameworks.add('GDPR');
        }
        if (type.includes('soc2') || type.includes('confidential') || type.includes('access')) {
          frameworks.add('SOC2');
        }
        if (type.includes('iso') || type.includes('27001')) {
          frameworks.add('ISO27001');
        }
        if (type.includes('nist')) {
          frameworks.add('NIST80053');
        }
      }
    });

    return Array.from(frameworks);
  };

  const getFrameworkColor = (framework: string): 'error' | 'warning' | 'info' | 'success' => {
    switch (framework) {
      case 'HIPAA':
        return 'error';
      case 'GDPR':
        return 'warning';
      case 'SOC2':
        return 'info';
      case 'ISO27001':
        return 'success';
      case 'NIST80053':
        return 'info';
      default:
        return 'info';
    }
  };

  const getFrameworkIcon = (framework: string) => {
    switch (framework) {
      case 'HIPAA':
        return <Shield fontSize="small" />;
      case 'GDPR':
        return <Gavel fontSize="small" />;
      case 'SOC2':
        return <Security fontSize="small" />;
      default:
        return <Security fontSize="small" />;
    }
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Calculate summary statistics
  const summaryStats = {
    total: totalCount,
    blocked: prompts.filter((p) => p.status === 'blocked').length,
    flagged: prompts.filter((p) => p.status === 'flagged').length,
    allowed: prompts.filter((p) => p.status === 'allowed').length,
    avgThreatScore:
      prompts.length > 0
        ? ((prompts.reduce((sum, p) => sum + p.threat_score, 0) / prompts.length) * 100).toFixed(1)
        : '0.0'
  };

  if (loading && prompts.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: { xs: 2, sm: 3, md: 4 } }}>
      {/* Enhanced Header Section */}
      <Box
        sx={{
          mb: 4,
          p: { xs: 2, sm: 3, md: 4 },
          borderRadius: 3,
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(theme.palette.secondary.main, 0.08)} 100%)`,
          border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '4px',
            background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`
          }
        }}
      >
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          alignItems="center"
          justifyContent="space-between"
        >
          <Stack direction="row" spacing={2} alignItems="center">
            <Avatar
              sx={{
                width: 64,
                height: 64,
                bgcolor: alpha(theme.palette.primary.main, 0.15),
                color: 'primary.main',
                border: `3px solid ${theme.palette.primary.main}`
              }}
            >
              <HistoryIcon sx={{ fontSize: 36 }} />
            </Avatar>
            <Box>
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 700,
                  background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  fontSize: { xs: '1.75rem', sm: '2.5rem' },
                  mb: 1
                }}
              >
                Prompt History
              </Typography>
              <Typography
                variant="body1"
                color="text.secondary"
                sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}
              >
                View and analyze your prompt processing history with comprehensive security insights
              </Typography>
            </Box>
          </Stack>
          <Tooltip title="Refresh history">
            <IconButton
              onClick={fetchPromptHistory}
              sx={{
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.2) }
              }}
            >
              <Refresh />
            </IconButton>
          </Tooltip>
        </Stack>
      </Box>

      {/* Summary Statistics Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          {
            label: 'Total Prompts',
            value: summaryStats.total,
            icon: <Assessment />,
            color: 'primary' as const
          },
          {
            label: 'Blocked',
            value: summaryStats.blocked,
            icon: <Block />,
            color: 'error' as const
          },
          {
            label: 'Flagged',
            value: summaryStats.flagged,
            icon: <Warning />,
            color: 'warning' as const
          },
          {
            label: 'Allowed',
            value: summaryStats.allowed,
            icon: <CheckCircle />,
            color: 'success' as const
          },
          {
            label: 'Avg Threat Score',
            value: `${summaryStats.avgThreatScore}%`,
            icon: <TrendingUp />,
            color: 'info' as const
          }
        ].map((stat, index) => (
          <Grid item xs={6} sm={6} md={2.4} key={index}>
            <Zoom in timeout={300 + index * 100}>
              <Card
                elevation={2}
                sx={{
                  borderRadius: 2,
                  border: `2px solid ${alpha(theme.palette[stat.color].main, 0.3)}`,
                  background: `linear-gradient(135deg, ${alpha(theme.palette[stat.color].main, 0.05)} 0%, ${alpha(theme.palette[stat.color].main, 0.12)} 100%)`,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: `0 8px 16px ${alpha(theme.palette[stat.color].main, 0.2)}`
                  }
                }}
              >
                <CardContent sx={{ p: 2, textAlign: 'center' }}>
                  <Box sx={{ color: `${stat.color}.main`, mb: 1 }}>{stat.icon}</Box>
                  <Typography
                    variant="h5"
                    sx={{ fontWeight: 700, color: `${stat.color}.main`, mb: 0.5 }}
                  >
                    {stat.value}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}
                  >
                    {stat.label}
                  </Typography>
                </CardContent>
              </Card>
            </Zoom>
          </Grid>
        ))}
      </Grid>

      {/* Enhanced Filters */}
      <Paper
        elevation={2}
        sx={{
          p: 3,
          mb: 3,
          borderRadius: 3,
          background:
            theme.palette.mode === 'dark' ? alpha(theme.palette.background.paper, 0.6) : '#ffffff',
          border: `1px solid ${alpha(theme.palette.divider, 0.1)}`
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
          <FilterList sx={{ color: 'primary.main' }} />
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Filters
          </Typography>
        </Stack>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              select
              fullWidth
              label="Status Filter"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2
                }
              }}
            >
              <MenuItem value="all">All Statuses</MenuItem>
              <MenuItem value="allowed">
                <Stack direction="row" spacing={1} alignItems="center">
                  <CheckCircle fontSize="small" color="success" />
                  <span>Allowed</span>
                </Stack>
              </MenuItem>
              <MenuItem value="flagged">
                <Stack direction="row" spacing={1} alignItems="center">
                  <Warning fontSize="small" color="warning" />
                  <span>Flagged</span>
                </Stack>
              </MenuItem>
              <MenuItem value="blocked">
                <Stack direction="row" spacing={1} alignItems="center">
                  <Block fontSize="small" color="error" />
                  <span>Blocked</span>
                </Stack>
              </MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              select
              fullWidth
              label="Compliance Framework"
              value={frameworkFilter}
              onChange={(e) => setFrameworkFilter(e.target.value)}
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2
                }
              }}
            >
              <MenuItem value="all">All Frameworks</MenuItem>
              <MenuItem value="HIPAA">
                <Stack direction="row" spacing={1} alignItems="center">
                  <Shield fontSize="small" />
                  <span>HIPAA</span>
                </Stack>
              </MenuItem>
              <MenuItem value="GDPR">
                <Stack direction="row" spacing={1} alignItems="center">
                  <Gavel fontSize="small" />
                  <span>GDPR</span>
                </Stack>
              </MenuItem>
              <MenuItem value="SOC2">
                <Stack direction="row" spacing={1} alignItems="center">
                  <Security fontSize="small" />
                  <span>SOC2</span>
                </Stack>
              </MenuItem>
              <MenuItem value="ISO27001">
                <Stack direction="row" spacing={1} alignItems="center">
                  <VerifiedUser fontSize="small" />
                  <span>ISO 27001</span>
                </Stack>
              </MenuItem>
              <MenuItem value="NIST80053">
                <Stack direction="row" spacing={1} alignItems="center">
                  <Security fontSize="small" />
                  <span>NIST 800-53</span>
                </Stack>
              </MenuItem>
            </TextField>
          </Grid>
        </Grid>
      </Paper>

      {error && (
        <Fade in>
          <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
            {error}
          </Alert>
        </Fade>
      )}

      {/* Enhanced Table */}
      <Paper
        elevation={2}
        sx={{
          borderRadius: 3,
          overflow: 'hidden',
          border: `1px solid ${alpha(theme.palette.divider, 0.1)}`
        }}
      >
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow
                sx={{
                  background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(theme.palette.secondary.main, 0.08)} 100%)`,
                  '& th': {
                    fontWeight: 700,
                    fontSize: '0.875rem',
                    textTransform: 'uppercase',
                    letterSpacing: 0.5
                  }
                }}
              >
                <TableCell>Date & Time</TableCell>
                <TableCell>Prompt</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Compliance</TableCell>
                <TableCell>Threat Score</TableCell>
                <TableCell>Threats</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {prompts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Fade in>
                      <Box py={6}>
                        <Avatar
                          sx={{
                            width: 80,
                            height: 80,
                            margin: '0 auto',
                            mb: 2,
                            bgcolor: alpha(theme.palette.primary.main, 0.1),
                            color: 'primary.main'
                          }}
                        >
                          <HistoryIcon sx={{ fontSize: 48 }} />
                        </Avatar>
                        <Typography color="textSecondary" variant="h6" gutterBottom>
                          {loading ? 'Loading history...' : 'No prompt history available'}
                        </Typography>
                        <Typography color="textSecondary" variant="body2">
                          Start testing prompts to see your history here
                        </Typography>
                      </Box>
                    </Fade>
                  </TableCell>
                </TableRow>
              ) : (
                prompts
                  .filter((prompt) => {
                    // Filter by framework
                    if (frameworkFilter === 'all') return true;
                    const frameworks = getComplianceFrameworks(prompt.detected_threats || []);
                    return frameworks.includes(frameworkFilter);
                  })
                  .map((prompt, index) => {
                    const frameworks = getComplianceFrameworks(prompt.detected_threats || []);

                    return (
                      <Fade in timeout={200 + index * 50} key={prompt.id}>
                        <TableRow
                          hover
                          sx={{
                            '&:hover': {
                              bgcolor: alpha(theme.palette.primary.main, 0.04),
                              cursor: 'pointer'
                            },
                            transition: 'all 0.2s ease'
                          }}
                        >
                          <TableCell>
                            <Stack direction="row" spacing={1} alignItems="center">
                              <AccessTime sx={{ fontSize: 18, color: 'text.secondary' }} />
                              <Typography variant="body2">
                                {formatDate(prompt.created_at)}
                              </Typography>
                            </Stack>
                          </TableCell>
                          <TableCell>
                            <Tooltip title={prompt.prompt_preview} arrow>
                              <Typography
                                variant="body2"
                                sx={{
                                  maxWidth: 300,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap'
                                }}
                              >
                                {prompt.prompt_preview}
                              </Typography>
                            </Tooltip>
                          </TableCell>
                          <TableCell>
                            <Chip
                              icon={getStatusIcon(prompt.status)}
                              label={prompt.status.toUpperCase()}
                              color={getStatusColor(prompt.status) as any}
                              size="small"
                              sx={{
                                fontWeight: 700,
                                fontSize: '0.7rem',
                                letterSpacing: 0.5
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <Box display="flex" gap={0.5} flexWrap="wrap">
                              {frameworks.length > 0 ? (
                                frameworks.map((framework) => (
                                  <Chip
                                    key={framework}
                                    icon={getFrameworkIcon(framework)}
                                    label={framework}
                                    color={getFrameworkColor(framework)}
                                    size="small"
                                    variant="outlined"
                                    sx={{
                                      fontWeight: 600,
                                      fontSize: '0.7rem'
                                    }}
                                  />
                                ))
                              ) : (
                                <Typography variant="body2" color="textSecondary">
                                  None
                                </Typography>
                              )}
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={`${(prompt.threat_score * 100).toFixed(1)}%`}
                              color={getThreatColor(prompt.threat_score) as any}
                              size="small"
                              icon={<TrendingUp sx={{ fontSize: 14 }} />}
                              sx={{
                                fontWeight: 700,
                                fontSize: '0.75rem'
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            {prompt.threats_count > 0 ? (
                              <Chip
                                label={`${prompt.threats_count} threat${prompt.threats_count > 1 ? 's' : ''}`}
                                size="small"
                                variant="outlined"
                                color="error"
                                icon={<Warning sx={{ fontSize: 14 }} />}
                                sx={{
                                  fontWeight: 600,
                                  fontSize: '0.7rem'
                                }}
                              />
                            ) : (
                              <Chip
                                label="Clean"
                                size="small"
                                color="success"
                                variant="outlined"
                                icon={<CheckCircle sx={{ fontSize: 14 }} />}
                                sx={{
                                  fontWeight: 600,
                                  fontSize: '0.7rem'
                                }}
                              />
                            )}
                          </TableCell>
                          <TableCell align="center">
                            <Tooltip title="View Details" arrow>
                              <IconButton
                                size="small"
                                onClick={() => handleViewDetails(prompt.id)}
                                sx={{
                                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                                  '&:hover': {
                                    bgcolor: alpha(theme.palette.primary.main, 0.2),
                                    transform: 'scale(1.1)'
                                  },
                                  transition: 'all 0.2s ease'
                                }}
                              >
                                <Visibility fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      </Fade>
                    );
                  })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {prompts.length > 0 && (
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={totalCount}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{
              borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
              bgcolor: alpha(theme.palette.background.default, 0.5)
            }}
          />
        )}
      </Paper>

      {/* Enhanced Detail Dialog */}
      <Dialog
        open={!!selectedPrompt}
        onClose={handleCloseDetail}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            border: `2px solid ${alpha(theme.palette.primary.main, 0.2)}`
          }
        }}
      >
        <DialogTitle
          sx={{
            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(theme.palette.secondary.main, 0.08)} 100%)`,
            borderBottom: `2px solid ${alpha(theme.palette.primary.main, 0.2)}`
          }}
        >
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Stack direction="row" spacing={2} alignItems="center">
              <Avatar
                sx={{
                  bgcolor: alpha(theme.palette.primary.main, 0.15),
                  color: 'primary.main'
                }}
              >
                <Info />
              </Avatar>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Prompt Details
              </Typography>
            </Stack>
            <IconButton
              onClick={handleCloseDetail}
              size="small"
              sx={{
                bgcolor: alpha(theme.palette.error.main, 0.1),
                '&:hover': { bgcolor: alpha(theme.palette.error.main, 0.2) }
              }}
            >
              <Close />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ p: 3, mt: 2 }}>
          {detailLoading ? (
            <Box display="flex" justifyContent="center" alignItems="center" p={4}>
              <CircularProgress />
            </Box>
          ) : detailError ? (
            <Fade in>
              <Alert severity="error" sx={{ borderRadius: 2 }}>
                {detailError}
              </Alert>
            </Fade>
          ) : selectedPrompt ? (
            <Box>
              {/* Status and Metadata */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      background: `linear-gradient(135deg, ${alpha(theme.palette[getStatusColor(selectedPrompt.status)].main, 0.08)} 0%, ${alpha(theme.palette[getStatusColor(selectedPrompt.status)].main, 0.15)} 100%)`,
                      border: `2px solid ${alpha(theme.palette[getStatusColor(selectedPrompt.status)].main, 0.3)}`
                    }}
                  >
                    <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 700 }}>
                      Status
                    </Typography>
                    <Chip
                      icon={getStatusIcon(selectedPrompt.status)}
                      label={selectedPrompt.status.toUpperCase()}
                      color={getStatusColor(selectedPrompt.status) as any}
                      sx={{ fontWeight: 700, fontSize: '0.8rem' }}
                    />
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      background: `linear-gradient(135deg, ${alpha(theme.palette[getThreatColor(selectedPrompt.threat_score)].main, 0.08)} 0%, ${alpha(theme.palette[getThreatColor(selectedPrompt.threat_score)].main, 0.15)} 100%)`,
                      border: `2px solid ${alpha(theme.palette[getThreatColor(selectedPrompt.threat_score)].main, 0.3)}`
                    }}
                  >
                    <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 700 }}>
                      Threat Score
                    </Typography>
                    <Chip
                      icon={<TrendingUp />}
                      label={`${(selectedPrompt.threat_score * 100).toFixed(1)}%`}
                      color={getThreatColor(selectedPrompt.threat_score) as any}
                      sx={{ fontWeight: 700, fontSize: '0.8rem' }}
                    />
                  </Paper>
                </Grid>
              </Grid>

              {/* Processing Info */}
              <Paper
                elevation={0}
                sx={{
                  p: 2.5,
                  mb: 3,
                  borderRadius: 2,
                  background: alpha(theme.palette.info.main, 0.05),
                  border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`
                }}
              >
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                  <Speed sx={{ color: 'info.main' }} />
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    Processing Information
                  </Typography>
                </Stack>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Memory sx={{ fontSize: 18, color: 'text.secondary' }} />
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Tokens Used
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                          {selectedPrompt.tokens_used}
                        </Typography>
                      </Box>
                    </Stack>
                  </Grid>
                  <Grid item xs={6}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <AccessTime sx={{ fontSize: 18, color: 'text.secondary' }} />
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Processing Time
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                          {selectedPrompt.processing_time_ms}ms
                        </Typography>
                      </Box>
                    </Stack>
                  </Grid>
                </Grid>
              </Paper>

              {/* Detected Threats */}
              {selectedPrompt.detected_threats && selectedPrompt.detected_threats.length > 0 && (
                <Paper
                  elevation={0}
                  sx={{
                    p: 2.5,
                    mb: 3,
                    borderRadius: 2,
                    background: `linear-gradient(135deg, ${alpha(theme.palette.error.main, 0.05)} 0%, ${alpha(theme.palette.error.main, 0.12)} 100%)`,
                    border: `2px solid ${alpha(theme.palette.error.main, 0.3)}`
                  }}
                >
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                    <Warning sx={{ color: 'error.main' }} />
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'error.main' }}>
                      Detected Threats ({selectedPrompt.detected_threats.length})
                    </Typography>
                  </Stack>
                  <List dense>
                    {selectedPrompt.detected_threats.map((threat, index) => (
                      <ListItem
                        key={index}
                        sx={{
                          bgcolor: alpha(theme.palette.background.paper, 0.6),
                          borderRadius: 1,
                          mb: 1,
                          border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`
                        }}
                      >
                        <ListItemIcon>
                          <Warning color="warning" />
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {threat.type.replace(/_/g, ' ').toUpperCase()}
                            </Typography>
                          }
                          secondary={
                            <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
                              <Chip
                                label={`Confidence: ${(threat.confidence * 100).toFixed(1)}%`}
                                size="small"
                                color="warning"
                                sx={{ fontSize: '0.7rem' }}
                              />
                              {threat.category && (
                                <Chip
                                  label={threat.category}
                                  size="small"
                                  variant="outlined"
                                  sx={{ fontSize: '0.7rem' }}
                                />
                              )}
                            </Stack>
                          }
                        />
                      </ListItem>
                    ))}
                  </List>
                </Paper>
              )}

              {/* Original Prompt */}
              <Paper
                elevation={0}
                sx={{
                  p: 2.5,
                  mb: 2,
                  borderRadius: 2,
                  border: `1px solid ${alpha(theme.palette.divider, 0.2)}`
                }}
              >
                <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 700, mb: 1.5 }}>
                  Original Prompt
                </Typography>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    borderRadius: 1,
                    backgroundColor: alpha(theme.palette.grey[500], 0.05),
                    border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                    maxHeight: 200,
                    overflow: 'auto',
                    fontFamily: 'monospace',
                    fontSize: '0.875rem',
                    whiteSpace: 'pre-wrap',
                    lineHeight: 1.6
                  }}
                >
                  {selectedPrompt.prompt_text}
                </Paper>
              </Paper>

              {/* Filtered Prompt (if different) */}
              {selectedPrompt.filtered_prompt &&
                selectedPrompt.filtered_prompt !== selectedPrompt.prompt_text && (
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2.5,
                      mb: 2,
                      borderRadius: 2,
                      background: `linear-gradient(135deg, ${alpha(theme.palette.warning.main, 0.05)} 0%, ${alpha(theme.palette.warning.main, 0.12)} 100%)`,
                      border: `2px solid ${alpha(theme.palette.warning.main, 0.3)}`
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      gutterBottom
                      sx={{ fontWeight: 700, mb: 1.5, color: 'warning.main' }}
                    >
                      Filtered Prompt (PII Redacted)
                    </Typography>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2,
                        borderRadius: 1,
                        backgroundColor: alpha(theme.palette.warning.main, 0.08),
                        border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}`,
                        maxHeight: 200,
                        overflow: 'auto',
                        fontFamily: 'monospace',
                        fontSize: '0.875rem',
                        whiteSpace: 'pre-wrap',
                        lineHeight: 1.6
                      }}
                    >
                      {selectedPrompt.filtered_prompt}
                    </Paper>
                  </Paper>
                )}

              {/* Response */}
              {selectedPrompt.response_text && (
                <Paper
                  elevation={0}
                  sx={{
                    p: 2.5,
                    mb: 2,
                    borderRadius: 2,
                    border: `1px solid ${alpha(theme.palette.divider, 0.2)}`
                  }}
                >
                  <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 700, mb: 1.5 }}>
                    LLM Response
                  </Typography>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      borderRadius: 1,
                      backgroundColor: alpha(theme.palette.grey[500], 0.05),
                      border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                      maxHeight: 300,
                      overflow: 'auto',
                      fontFamily: 'monospace',
                      fontSize: '0.875rem',
                      whiteSpace: 'pre-wrap',
                      lineHeight: 1.6
                    }}
                  >
                    {selectedPrompt.response_text}
                  </Paper>
                </Paper>
              )}

              {/* Filtered Response (if different) */}
              {selectedPrompt.filtered_response &&
                selectedPrompt.filtered_response !== selectedPrompt.response_text && (
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2.5,
                      mb: 2,
                      borderRadius: 2,
                      background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.05)} 0%, ${alpha(theme.palette.success.main, 0.12)} 100%)`,
                      border: `2px solid ${alpha(theme.palette.success.main, 0.3)}`
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      gutterBottom
                      sx={{ fontWeight: 700, mb: 1.5, color: 'success.main' }}
                    >
                      Filtered Response (PII Redacted)
                    </Typography>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2,
                        borderRadius: 1,
                        backgroundColor: alpha(theme.palette.success.main, 0.08),
                        border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
                        maxHeight: 300,
                        overflow: 'auto',
                        fontFamily: 'monospace',
                        fontSize: '0.875rem',
                        whiteSpace: 'pre-wrap',
                        lineHeight: 1.6
                      }}
                    >
                      {selectedPrompt.filtered_response}
                    </Paper>
                  </Paper>
                )}

              {/* Timestamp */}
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 2 }}>
                <AccessTime sx={{ fontSize: 16, color: 'text.secondary' }} />
                <Typography variant="caption" color="textSecondary">
                  Created: {formatDate(selectedPrompt.created_at)}
                </Typography>
              </Stack>
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions
          sx={{
            p: 2,
            borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            bgcolor: alpha(theme.palette.background.default, 0.5)
          }}
        >
          <Button
            onClick={handleCloseDetail}
            variant="contained"
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default PromptHistory;
